/**
 * App-wide config. Set REACT_APP_DEBUG_MODE=true in .env.development.local for local hot-reload dev.
 * Production builds ignore .env.development.local — only npm start uses debug settings.
 */

import { Capacitor } from '@capacitor/core';

export const DEBUG_MODE = process.env.REACT_APP_DEBUG_MODE === 'true';

const PRODUCTION_API_ORIGIN = 'https://tbviewermd.traqbeat.com';
const DEV_API_ORIGIN = String(process.env.REACT_APP_BACKEND_URL || 'http://localhost').replace(/\/$/, '');

/** True when running inside a Capacitor native shell (iOS or Android). */
export const isNativeCapacitorRuntime = Capacitor.isNativePlatform();

/** @deprecated Use isNativeCapacitorRuntime */
export const isIosCapacitorRuntime = isNativeCapacitorRuntime;

export const CAPTCHA_ENABLED =
  !DEBUG_MODE && Boolean(String(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim());

export function isRecaptchaErrorMessage(message: unknown): boolean {
  const text = String(message || '').toLowerCase();
  return text.includes('recaptcha') || text.includes('captcha');
}

/** Whether login/register must collect and send a reCAPTCHA token. */
export function shouldEnforceCaptcha(
  captchaRequired: boolean,
  serverDebugMode = false
): boolean {
  return !DEBUG_MODE && !serverDebugMode && CAPTCHA_ENABLED && captchaRequired;
}

/** Hide captcha-related API errors while DEBUG_MODE is active. */
export function sanitizeAuthError(
  message: unknown,
  fallback: string,
  serverDebugMode = false
): string {
  const text = String(message || '').trim();
  if (!text) {
    return fallback;
  }
  if ((DEBUG_MODE || serverDebugMode || !CAPTCHA_ENABLED) && isRecaptchaErrorMessage(text)) {
    return fallback;
  }
  return text;
}

/** Base path for API calls (includes /api). Web uses relative /api; native app uses full origin. */
export function getApiBaseUrl(): string {
  if (isNativeCapacitorRuntime) {
    return `${PRODUCTION_API_ORIGIN}/api`;
  }
  if (DEBUG_MODE) {
    return `${DEV_API_ORIGIN}/api`;
  }
  return '/api';
}

export function isAdminWebEmail(email?: string | null): boolean {
  return Boolean(email && email.trim().toLowerCase().endsWith('@admin.com'));
}

export function isSecurityAdminEmail(email?: string | null): boolean {
  return String(email || '').trim().toLowerCase() === 'security@admin.com';
}

export function isFetchRawAdminEmail(email?: string | null): boolean {
  return String(email || '').trim().toLowerCase() === 'fetchraw@admin.com';
}

/** Build a full API URL from a path like /users/login or users/login */
export function apiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const suffix = normalizedPath.startsWith('/api/')
    ? normalizedPath.slice(4)
    : normalizedPath;
  return `${getApiBaseUrl()}${suffix}`;
}
